import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Plus, Save, X, Wand2, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface EnhancedManualQuestionAdderProps {
  testId: string;
  testData: {
    subject: string;
    grade: string;
    topic: string;
  };
  onQuestionAdded: () => void;
}

interface QuestionData {
  text: string;
  type: 'multiple_choice' | 'matching' | 'true_false' | 'fill_blank' | 'puzzle';
  explanation: string;
  difficulty: 'easy' | 'medium' | 'hard';
  options?: Array<{ text: string; is_correct: boolean }>;
  pairs?: Array<{ left: string; right: string }>;
  blanks?: string[];
  correct_answer?: boolean | string;
}

export default function EnhancedManualQuestionAdder({ testId, testData, onQuestionAdded }: EnhancedManualQuestionAdderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [questionData, setQuestionData] = useState<QuestionData>({
    text: "",
    type: "multiple_choice",
    explanation: "",
    difficulty: "medium",
    options: [
      { text: "", is_correct: true },
      { text: "", is_correct: false }
    ]
  });

  const questionTypes = [
    { value: 'multiple_choice', label: 'Multiple Choice', description: 'Traditional A, B, C, D questions' },
    { value: 'matching', label: 'Matching Pairs', description: 'Match terms with definitions' },
    { value: 'true_false', label: 'True/False', description: 'Statement evaluation questions' },
    { value: 'fill_blank', label: 'Fill in the Blank', description: 'Complete the sentence questions' },
    { value: 'puzzle', label: 'Puzzle/Logic', description: 'Problem-solving questions' }
  ];

  const generateAIQuestion = async (type: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('generate-ai-questions', {
        body: {
          topic: testData.topic,
          grade: testData.grade,
          subject: testData.subject,
          questionTypes: [type],
          numberOfQuestions: 1,
          difficulty: questionData.difficulty
        }
      });
      
      if (error) throw error;
      
      if (data.questions && data.questions.length > 0) {
        const aiQuestion = data.questions[0];
        
        // Enhanced mapping for all question types
        const updatedQuestionData: any = {
          text: aiQuestion.question || "",
          explanation: aiQuestion.explanation || "",
          type: type as any,
          difficulty: questionData.difficulty
        };

        // Handle different question types with proper answer mapping
        switch (type) {
          case 'multiple_choice':
            if (aiQuestion.options && Array.isArray(aiQuestion.options)) {
              updatedQuestionData.options = aiQuestion.options.map((opt: string, idx: number) => ({
                text: opt,
                is_correct: idx === aiQuestion.correct_answer
              }));
            }
            break;
            
          case 'matching':
            if (aiQuestion.pairs && Array.isArray(aiQuestion.pairs)) {
              updatedQuestionData.pairs = aiQuestion.pairs;
            }
            break;
            
          case 'true_false':
            updatedQuestionData.correct_answer = aiQuestion.correct_answer;
            break;
            
          case 'fill_blank':
            if (aiQuestion.blanks && Array.isArray(aiQuestion.blanks)) {
              updatedQuestionData.blanks = aiQuestion.blanks;
            }
            break;
            
          case 'puzzle':
            if (aiQuestion.puzzle_data) {
              updatedQuestionData.correct_answer = aiQuestion.puzzle_data.solution || aiQuestion.correct_answer;
            } else {
              updatedQuestionData.correct_answer = aiQuestion.correct_answer || "";
            }
            break;
        }

        setQuestionData(prev => ({
          ...prev,
          ...updatedQuestionData
        }));
        
        toast.success('AI question with answers generated!');
      }
    } catch (error) {
      console.error('Error generating AI question:', error);
      toast.error('Failed to generate AI question');
    }
  };

  const handleSave = async () => {
    if (!questionData.text.trim()) {
      toast.error('Question text is required');
      return;
    }

    // Validate content for inappropriate words
    const { validateContent } = await import('@/utils/contentValidation');
    
    const questionValidation = validateContent(questionData.text);
    if (!questionValidation.isValid) {
      toast.error(`Question: ${questionValidation.message}`);
      return;
    }

    if (questionData.explanation.trim()) {
      const explanationValidation = validateContent(questionData.explanation);
      if (!explanationValidation.isValid) {
        toast.error(`Explanation: ${explanationValidation.message}`);
        return;
      }
    }

    // Validate type-specific content
    if (questionData.type === 'multiple_choice' && questionData.options) {
      for (const option of questionData.options) {
        const optionValidation = validateContent(option.text);
        if (!optionValidation.isValid) {
          toast.error(`Option content: ${optionValidation.message}`);
          return;
        }
      }
    }

    if (questionData.type === 'matching' && questionData.pairs) {
      for (const pair of questionData.pairs) {
        const leftValidation = validateContent(pair.left);
        if (!leftValidation.isValid) {
          toast.error(`Matching term: ${leftValidation.message}`);
          return;
        }
        const rightValidation = validateContent(pair.right);
        if (!rightValidation.isValid) {
          toast.error(`Matching definition: ${rightValidation.message}`);
          return;
        }
      }
    }

    if (questionData.type === 'fill_blank' && questionData.blanks) {
      for (const blank of questionData.blanks) {
        const blankValidation = validateContent(blank);
        if (!blankValidation.isValid) {
          toast.error(`Fill-in-the-blank answer: ${blankValidation.message}`);
          return;
        }
      }
    }

    if (questionData.type === 'puzzle' && typeof questionData.correct_answer === 'string') {
      const answerValidation = validateContent(questionData.correct_answer);
      if (!answerValidation.isValid) {
        toast.error(`Puzzle answer: ${answerValidation.message}`);
        return;
      }
    }

    try {
      // Get current question count for ordering
      const { data: existingQuestions } = await supabase
        .from('instructor_test_questions')
        .select('id')
        .eq('test_id', testId);

      const nextOrder = (existingQuestions?.length || 0) + 1;
      
      const { error } = await supabase
        .from('instructor_test_questions')
        .insert({
          test_id: testId,
          question_order: nextOrder,
          points: 1,
          question_type: questionData.type,
          custom_question_data: {
            text: questionData.text.trim(),
            type: questionData.type,
            explanation: questionData.explanation.trim() || null,
            difficulty: questionData.difficulty,
            options: questionData.options,
            pairs: questionData.pairs,
            blanks: questionData.blanks,
            correct_answer: questionData.correct_answer
          }
        });

      if (error) throw error;

      // Update the test's total_questions count
      await supabase
        .from('instructor_tests')
        .update({ total_questions: nextOrder })
        .eq('id', testId);

      toast.success('Question added successfully!');
      setIsOpen(false);
      resetForm();
      onQuestionAdded();
    } catch (error) {
      console.error('Error saving question:', error);
      toast.error('Failed to save question');
    }
  };

  const resetForm = () => {
    setQuestionData({
      text: "",
      type: "multiple_choice",
      explanation: "",
      difficulty: "medium",
      options: [
        { text: "", is_correct: true },
        { text: "", is_correct: false }
      ]
    });
  };

  const renderQuestionTypeEditor = () => {
    switch (questionData.type) {
      case 'multiple_choice':
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Answer Options *</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={() => setQuestionData(prev => ({
                  ...prev,
                  options: [...(prev.options || []), { text: "", is_correct: false }]
                }))}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Option
              </Button>
            </div>
            <RadioGroup className="space-y-3">
              {questionData.options?.map((option, index) => (
                <div key={index} className="flex items-start space-x-3 border p-3 rounded">
                  <RadioGroupItem
                    value={index.toString()}
                    checked={option.is_correct}
                    onClick={() => {
                      setQuestionData(prev => ({
                        ...prev,
                        options: prev.options?.map((opt, i) => ({
                          ...opt,
                          is_correct: i === index
                        }))
                      }));
                    }}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <Label className="text-sm font-medium">
                      Option {String.fromCharCode(65 + index)}
                      {option.is_correct && " (Correct Answer)"}
                    </Label>
                    <Input
                      placeholder={`Enter option ${String.fromCharCode(65 + index)}`}
                      value={option.text}
                      onChange={(e) => {
                        setQuestionData(prev => ({
                          ...prev,
                          options: prev.options?.map((opt, i) => 
                            i === index ? { ...opt, text: e.target.value } : opt
                          )
                        }));
                      }}
                      className="mt-1"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if ((questionData.options?.length || 0) > 2) {
                        setQuestionData(prev => ({
                          ...prev,
                          options: prev.options?.filter((_, i) => i !== index)
                        }));
                      }
                    }}
                    disabled={(questionData.options?.length || 0) <= 2}
                    className="mt-1"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </RadioGroup>
          </div>
        );
      
      case 'true_false':
        return (
          <div className="space-y-3">
            <Label>Correct Answer *</Label>
            <RadioGroup 
              value={questionData.correct_answer?.toString()}
              onValueChange={(value) => setQuestionData(prev => ({
                ...prev,
                correct_answer: value === 'true'
              }))}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="true" id="true" />
                <Label htmlFor="true">True</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="false" id="false" />
                <Label htmlFor="false">False</Label>
              </div>
            </RadioGroup>
          </div>
        );

      case 'matching':
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Matching Pairs *</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={() => setQuestionData(prev => ({
                  ...prev,
                  pairs: [...(prev.pairs || []), { left: "", right: "" }]
                }))}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Pair
              </Button>
            </div>
            {questionData.pairs?.map((pair, index) => (
              <div key={index} className="grid grid-cols-2 gap-3 p-3 border rounded">
                <div>
                  <Label className="text-sm">Term/Question</Label>
                  <Input
                    placeholder="Enter term or question"
                    value={pair.left}
                    onChange={(e) => {
                      setQuestionData(prev => ({
                        ...prev,
                        pairs: prev.pairs?.map((p, i) => 
                          i === index ? { ...p, left: e.target.value } : p
                        )
                      }));
                    }}
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label className="text-sm">Definition/Answer</Label>
                    <Input
                      placeholder="Enter definition or answer"
                      value={pair.right}
                      onChange={(e) => {
                        setQuestionData(prev => ({
                          ...prev,
                          pairs: prev.pairs?.map((p, i) => 
                            i === index ? { ...p, right: e.target.value } : p
                          )
                        }));
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setQuestionData(prev => ({
                        ...prev,
                        pairs: prev.pairs?.filter((_, i) => i !== index)
                      }));
                    }}
                    className="mt-5"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        );

      case 'fill_blank':
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Fill-in-the-Blank Answers *</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={() => setQuestionData(prev => ({
                  ...prev,
                  blanks: [...(prev.blanks || []), ""]
                }))}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Blank
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Use underscores (_____) in your question to indicate where blanks should appear.
            </p>
            {questionData.blanks?.map((blank, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  placeholder={`Answer for blank ${index + 1}`}
                  value={blank}
                  onChange={(e) => {
                    setQuestionData(prev => ({
                      ...prev,
                      blanks: prev.blanks?.map((b, i) => 
                        i === index ? e.target.value : b
                      )
                    }));
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setQuestionData(prev => ({
                      ...prev,
                      blanks: prev.blanks?.filter((_, i) => i !== index)
                    }));
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        );

      case 'puzzle':
        return (
          <div className="space-y-3">
            <Label>Correct Answer *</Label>
            <Input
              placeholder="Enter the correct answer to the puzzle"
              value={questionData.correct_answer as string || ""}
              onChange={(e) => setQuestionData(prev => ({
                ...prev,
                correct_answer: e.target.value
              }))}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          Add Manually
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Question</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Question Type Selection */}
          <div className="space-y-2">
            <Label>Question Type *</Label>
            <Select 
              value={questionData.type} 
              onValueChange={(value: any) => {
                setQuestionData(prev => ({
                  ...prev,
                  type: value,
                  // Reset type-specific data
                  options: value === 'multiple_choice' ? [
                    { text: "", is_correct: true },
                    { text: "", is_correct: false }
                  ] : undefined,
                  pairs: value === 'matching' ? [{ left: "", right: "" }] : undefined,
                  blanks: value === 'fill_blank' ? [""] : undefined,
                  correct_answer: value === 'true_false' ? false : (value === 'puzzle' ? "" : undefined)
                }));
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {questionTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div>
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-muted-foreground">{type.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Difficulty */}
          <div className="space-y-2">
            <Label>Difficulty Level</Label>
            <Select 
              value={questionData.difficulty} 
              onValueChange={(value: any) => setQuestionData(prev => ({ ...prev, difficulty: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Question Text */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="question-text">Question Text *</Label>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => generateAIQuestion(questionData.type)}
                >
                  <Wand2 className="h-4 w-4 mr-1" />
                  Generate with AI
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => generateAIQuestion('multiple_choice')}
                >
                  <Shuffle className="h-4 w-4 mr-1" />
                  Random AI
                </Button>
              </div>
            </div>
            <Textarea
              id="question-text"
              placeholder="Enter your question..."
              value={questionData.text}
              onChange={(e) => setQuestionData(prev => ({ ...prev, text: e.target.value }))}
              className="min-h-[100px]"
            />
          </div>

          {/* Question Type Specific Editor */}
          {renderQuestionTypeEditor()}

          {/* Explanation */}
          <div className="space-y-2">
            <Label htmlFor="explanation">Explanation (Optional)</Label>
            <Textarea
              id="explanation"
              placeholder="Explain the correct answer..."
              value={questionData.explanation}
              onChange={(e) => setQuestionData(prev => ({ ...prev, explanation: e.target.value }))}
            />
          </div>

          <Separator />
          
          {/* Actions */}
          <div className="flex justify-end space-x-2">
            <Button 
              variant="outline" 
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save Question
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}